import "./toolbarItems.css";

import { mm } from "@inovitas/infra3dsdk";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Close,
  Delete,
  EditOutlined,
  KeyboardBackspace,
  Remove,
  SaveAsOutlined,
  ShareLocation,
  ZoomInMap,
  ZoomOutMap,
} from "@mui/icons-material";
import { TreeView } from "@mui/x-tree-view";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import { Sorter } from "shared/common/utils/layers/layerutils";
import EditContent from "./editContent/EditContent";
import EditFeatureList from "./editContent/EditFeatureList";
import FeatureListTable from "./featureTable/FeatureListTable";
import SingleFeatureTable from "./featureTable/SingleFeatureTable";
import AddLayerContent from "./layerContent/AddLayerContent";
import { LayertreeGroups } from "./layertreeGroups/LayertreeGroups";
import { LayertreeLayers } from "./layertreeGroups/LayertreeLayers";

interface props {
  authMode: "view" | "edit";
}

export default function LayerTree({ authMode }: props) {
  const apm = defaultStore((state) => state.apm);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const editLayerSettings = defaultStore((state) => state.editLayerSettings);
  const changeDeleteFeatureWithID = defaultStore(
    (state) => state.changeDeleteFeatureWithID
  );
  const changeAlertDeleteFeature = defaultStore(
    (state) => state.changeAlertDeleteFeature
  );
  const changeAlertSaveOrDiscardLastLayer = defaultStore(
    (state) => state.changeAlertSaveOrDiscardLastLayer
  );
  const changeAlertSaveLayer = defaultStore(
    (state) => state.changeAlertSaveLayer
  );

  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const selectedTableRows = sidebarStore((state) => state.selectedTableRows);
  const topics = sidebarStore((state) => state.topics);
  const editPopover = sidebarStore((state) => state.editPopover);
  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );
  const changeEditPopover = sidebarStore((state) => state.changeEditPopover);
  const selectTools = sidebarStore((state) => state.selectTools);
  const showDescription = sidebarStore((state) => state.showDescription);
  const changeShowDescription = sidebarStore(
    (state) => state.changeShowDescription
  );
  const enableEditFeature = sidebarStore((state) => state.enableEditFeature);
  const changeEnableEditFeature = sidebarStore(
    (state) => state.changeEnableEditFeature
  );
  const enableDeleteFeature = sidebarStore(
    (state) => state.enableDeleteFeature
  );
  const changeEnableDeleteFeature = sidebarStore(
    (state) => state.changeEnableDeleteFeature
  );
  const featureTableAddedRows = sidebarStore(
    (state) => state.featureTableAddedRows
  );
  const expandedTopics = sidebarStore((state) => state.expandedTopics);
  const changeExpandedTopics = sidebarStore(
    (state) => state.changeExpandedTopics
  );
  const editToolActivated = sidebarStore((state) => state.editToolActivated);
  const activeEditModeLayers = sidebarStore(
    (state) => state.activeEditModeLayers
  );
  const changeActiveEditModeLayers = sidebarStore(
    (state) => state.changeActiveEditModeLayers
  );
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );
  const changeEditMode = sidebarStore((state) => state.changeEditMode);
  const expandAddLayersList = sidebarStore(
    (state) => state.expandAddLayersList
  );
  const changeExpandAddLayersList = sidebarStore(
    (state) => state.changeExpandAddLayersList
  );
  const infosPopover = sidebarStore((state) => state.infosPopover);
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const changeInfosPopover = sidebarStore((state) => state.changeInfosPopover);
  const changeWebServiceLayer = sidebarStore(
    (state) => state.changeWebServiceLayer
  );
  const changeWebServiceLayerStep = sidebarStore(
    (state) => state.changeWebServiceLayerStep
  );
  const changeActiveAddLayersListItem = sidebarStore(
    (state) => state.changeActiveAddLayersListItem
  );
  const showEditToolsButtons = sidebarStore(
    (state) => state.showEditToolsButtons
  );
  const changeShowEditToolsButtons = sidebarStore(
    (state) => state.changeShowEditToolsButtons
  );

  const [topic, setTopic] = useState<Topics[]>([]);

  const [disableEditMode, setDisableEditMode] = useState<boolean>(false);

  const [layerMenuID, setLayerMenuID] = useState<string>("");

  const [featureTableSorter, setFeatureTableSorter] = useState<Sorter>();

  const [featureTablePageNumber, setFeatureTablePageNumber] =
    useState<number>(1);

  const [featureTableNumberPerPage, setFeatureTableNumberPerPage] =
    useState<number>(5);

  const geonisPopupRef = useRef<Window | null>(null);

  const { t } = useTranslation();

  const activeLayer = UseActiveLayer(viewer);

  const showGeonisMask = useMemo(() => {
    return activeLayer?.attributemask.type == "geonis";
  }, [activeLayer]);

  //close layer tree and reset state-s
  const handleClose = () => {
    closeAllToolPopover();
    changeWebServiceLayer({});
    changeWebServiceLayerStep(0);
    changeActiveAddLayersListItem("");
    changeSelectedTableRows(undefined);
    if (viewer) {
      viewer
        .getScene()
        .getLayerHandler()
        .getActiveLayer()
        ?.featureselector.clearSelection();
      viewer.getScene().getLayerHandler().setActiveLayer(null);
    }
  };

  // open or close add layers list
  const handleAddLayersList = () => {
    changeExpandAddLayersList(!expandAddLayersList);
    changeMaximizeTable(!maximizeTable);
  };

  // change table size
  const handleChangeTableSize = () => {
    changeInfosPopover(false);

    if (editPopover) {
      changeShowEditToolsButtons(!showEditToolsButtons);
    } else {
      changeMaximizeTable(!maximizeTable);
    }
  };

  //close active layer
  const handleCloseFeatureInfo = () => {
    if (selectedTableRows && editPopover) {
      handleCloseFeatureTable();
    }
    handleRemoveActiveFeature();
    changeSelectedTableRows(undefined);
  };

  // remove from selection active feature
  const handleRemoveActiveFeature = () => {
    if (viewer === undefined) return;
    if (activeLayer) {
      activeLayer.featureselector.clearSelection();
    }
  };

  //save changes
  const saveChanges = () => {
    if (viewer === undefined) return;
    changeAlertSaveLayer(true);
  };

  // close feature table and reset state-s
  const handleCloseFeatureTable = () => {
    if (maximizeTable) {
      handleChangeTableSize();
    }
    if (editPopover && !showDescription) {
      changeEditPopover(false);
      changeShowDescription(!showDescription);
    }
    viewer?.getScene().getLayerHandler().setActiveLayer(null);
  };

  //close feature table after removing a layer
  const handleCloseEverythingOnRemoveLayer = () => {
    handleCloseFeatureTable();
    changeSelectedTableRows(undefined);
  };

  //toggle a tree item
  function handleTreeToggle(event: React.SyntheticEvent, nodeIds: string[]) {
    changeExpandedTopics(nodeIds);
  }

  //enable edit feature tool
  const enableEdit = () => {
    changeEnableEditFeature(!enableEditFeature);
    changeEnableDeleteFeature(false);
  };

  //enable delete feature tool
  const enableDelete = () => {
    changeEnableDeleteFeature(!enableDeleteFeature);
    changeEnableEditFeature(false);
  };

  //disable delete and edit feature tools
  const disableEvents = () => {
    changeEnableDeleteFeature(false);
    changeEnableEditFeature(false);
  };

  //delete single feature
  const deleteSingleFeature = (featureID: string) => {
    changeEnableEditFeature(true);
    changeAlertDeleteFeature(true);
    changeDeleteFeatureWithID(featureID);
    if (infosPopover) {
      changeInfosPopover(false);
    }
  };

  //scroll to active layer
  useEffect(() => {
    if (!viewer) return;
    if (activeLayer !== null) {
      expandAndScrollToLayer(activeLayer);
    }
  }, [
    activeLayer,
    selectedTableRows,
    document.getElementById("infra3d-layertree")?.offsetHeight,
  ]);

  //scroll to layer that is selected
  function expandAndScrollToLayer(layer: mm.Layer) {
    const key: string | undefined = topics.find(
      (item: Topics) => item.layer && item.layer.id === layer.id
    )?.key;
    if (key && !expandedTopics.find((val: any) => val === key)) {
      const routeId = key.split("-")[0];
      const list: string[] = [...expandedTopics, routeId];
      changeExpandedTopics(list);
    }

    const containerElement = document.getElementById("infra3d-layertree");
    if (containerElement !== null) {
      const targetElements = Array.from(
        containerElement.getElementsByClassName("MuiTreeItem-label")
      );
      targetElements.forEach((ele) => {
        if (ele.innerHTML === layer.title && ele instanceof HTMLElement) {
          setTimeout(() => {
            // wait for animations to finish, then scroll into view
            ele.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }, 300);
        }
      });
    }
  }

  // creates folder routes for layers
  useEffect(() => {
    const allTopics: string[] = [];
    topics.forEach(
      (item: Topics) => item.layer && allTopics.push(item.layer.topic)
    );
    const folders = Array.from(new Set(allTopics));
    const route: Topics[] = [];
    folders.forEach((folderName, index) => {
      if (folderName === "") {
        const layersWithoutFolder: Topics[] = [];
        topics.forEach((layer: Topics, index: number) => {
          if (layer.layer && layer.layer.topic === "") {
            layer.key = "route-without-topic" + index;
            layersWithoutFolder.push(layer);
          }
        });
        route.push(...layersWithoutFolder);
      } else {
        const childrens: Topics[] = [];
        let visible = false;
        let hasChildren = false;
        let expanded = false;
        let addedFromUser = false;
        topics.forEach((layer: Topics, layerIndex: number) => {
          if (layer.layer && layer.layer.topic === folderName) {
            hasChildren = true;
            layer.key = "route" + index + "-layer" + layerIndex;
            childrens.push(layer);
            visible = visible || layer.layer.getVisible();
            if (layer.layer.expanded) {
              expanded = true;
            }
            if (layer.addedFromUser) {
              addedFromUser = true;
            }
          }
        });

        route.push({
          addedFromUser,
          name: folderName,
          visible,
          snappingEnabled:
            hasChildren && childrens.every((c) => c.snappingEnabled),
          folder: true,
          expanded,
          children: childrens,
          key: "route" + index,
          layer: undefined,
        });
      }
    });
    setTopic(route);
  }, [topics]);

  //disable delete and edit tools
  useEffect(() => {
    disableEvents();
  }, [selectedTableRows, activeLayer]);

  //change and update export file configration
  const changeExportFile = (id: string, visible: boolean) => {
    let firstArrayPosition;
    let secondArrayPosition;
    layerConfiguration.forEach((item: any, index: number) =>
      item.layers.forEach((layer: mm.Layer, layerIndex: number) => {
        if (layer.id === id) {
          firstArrayPosition = index;
          secondArrayPosition = layerIndex;
        }
      })
    );
    if (firstArrayPosition !== undefined && secondArrayPosition !== undefined) {
      layerConfiguration[firstArrayPosition].layers[
        secondArrayPosition
      ].visible = visible;
    }
  };

  useEffect(() => {
    if (!editPopover && selectedTableRows && selectedTableRows.id === null) {
      changeSelectedTableRows(undefined);
    }
    // if add layers form is open than close it on edit mode
    if (editPopover) {
      changeExpandAddLayersList(false);
    }
  }, [editPopover]);

  //enable or disable edit mode for active layer
  useEffect(() => {
    if (activeLayer) {
      changeFeatureTableAddedRows({
        length: 0,
        inserted: [],
        updated: [],
        deleted: [],
      });
      setFeatureTableSorter(undefined);
      setFeatureTablePageNumber(1);
      if (
        (activeLayer.geometrytype == mm.GEOMETRYTYPE.POINT ||
          activeLayer.geometrytype == mm.GEOMETRYTYPE.LINESTRING ||
          activeLayer.geometrytype == mm.GEOMETRYTYPE.POLYGON) &&
        activeLayer.editable
      ) {
        setDisableEditMode(false);
      } else {
        setDisableEditMode(true);
      }
      if (activeEditModeLayers && activeEditModeLayers?.getLayerEditor()) {
        handleActiveEditModeLayersList();
      }
    } else {
      if (activeEditModeLayers && activeEditModeLayers?.getLayerEditor()) {
        checkForLastLayerDiscardedChanges();
      }
    }
  }, [activeLayer]);

  //controll if last active layer have changes while was on edit mode than show message for discarded changes
  const checkForLastLayerDiscardedChanges = () => {
    if (!viewer) return;
    const haveChanges = activeEditModeLayers
      ? getChangedFeaturesList(activeEditModeLayers)
      : [];
    if (haveChanges.length > 0) {
      changeAlertSaveOrDiscardLastLayer(true);
    } else {
      changeActiveEditModeLayers(activeLayer);
    }
  };

  //deactivate edit mode when you select anothertype of layer
  const handleActiveEditModeLayersList = () => {
    if (!viewer) return;
    if (activeEditModeLayers && activeLayer) {
      if (activeEditModeLayers.id === activeLayer.id) {
        changeActiveEditModeLayers(activeLayer);
        return;
      } else {
        if (activeEditModeLayers.getLayerEditor()) {
          checkForLastLayerDiscardedChanges();
        }
        activeLayer.setEditMode(true);
        if (activeLayer.mode === 1 && enableEditFeature) {
          changeEditMode(true);
          changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
        } else {
          changeFeatureTableAddedRows({
            length: 0,
            inserted: [],
            updated: [],
            deleted: [],
          });
          activeEditModeLayers.setEditMode(false);
          changeEditMode(false);
        }
      }
    } else {
      changeActiveEditModeLayers(activeLayer);
    }
  };

  //manage keyboard events
  useEffect(() => {
    if (editLayerSettings) {
      manageKeyboardEvents(viewer, true);
    } else {
      manageKeyboardEvents(viewer, false);
    }
  }, [editLayerSettings]);

  // update the visibility and snapping in the UI in case it gets changed via API
  useEffect(() => {
    if (!viewer) return;

    const handleLayerUpdated = ([layer, updates]: [mm.Layer, any]) => {
      if (updates.visibility === undefined && updates.snappable === undefined)
        return;

      setTopic((prev) =>
        prev.map((item) => {
          if (item.folder) {
            const updatedChildren = item.children?.map((child) => {
              if (child.layer?.id !== layer.id) return child;
              return {
                ...child,
                ...(updates.visibility !== undefined && {
                  visible: updates.visibility,
                }),
                ...(updates.snappable !== undefined && {
                  snappingEnabled: updates.snappable,
                }),
              };
            });
            return {
              ...item,
              children: updatedChildren,
              visible: updatedChildren?.some((c) => c.visible) ?? item.visible,
              snappingEnabled:
                updatedChildren?.every((c) => c.snappingEnabled) ??
                item.snappingEnabled,
            };
          }
          if (item.layer?.id !== layer.id) return item;
          return {
            ...item,
            ...(updates.visibility !== undefined && {
              visible: updates.visibility,
            }),
            ...(updates.snappable !== undefined && {
              snappingEnabled: updates.snappable,
            }),
          };
        })
      );
    };

    viewer.on("layerupdated", handleLayerUpdated);

    return () => {
      viewer.off("layerupdated", handleLayerUpdated);
    };
  }, [viewer]);

  useEffect(() => {
    if (!showGeonisMask || !activeLayer || !selectedTableRows) {
      return;
    }

    const featureId = String(selectedTableRows.id);
    if (featureId === "null" || featureId === "undefined") {
      return;
    }

    const maskoptions = activeLayer.attributemask.options;

    let url =
      maskoptions.url_geonis +
      "/AttributeEditor/index.html?" +
      "project=" +
      maskoptions.geonisproject +
      "&datasource=" +
      maskoptions.geonisdatasource +
      "&" +
      "table=" +
      maskoptions.geonistablename +
      "&lang=" +
      maskoptions.geonismasklang +
      "&id=" +
      featureId +
      "&readonly=" +
      !activeLayer.editable;

    if (maskoptions.gtoken) {
      url = url + "&gtoken=" + maskoptions.gtoken;
    }

    const existingPopup = geonisPopupRef.current;
    if (existingPopup && !existingPopup.closed) {
      try {
        existingPopup.location.href = url;
        existingPopup.focus();
        return;
      } catch {
        existingPopup.close();
      }
    }

    const spec =
      "width=600, height=400, directories=0, status=0, titlebar=0, menubar=0, location=1, toolbar=0, scrollbars=1, resizable=1";

    geonisPopupRef.current = window.open(url, "_blank", spec);
  }, [showGeonisMask, activeLayer, selectedTableRows]);

  useEffect(() => {
    return () => {
      if (geonisPopupRef.current && !geonisPopupRef.current.closed) {
        geonisPopupRef.current.close();
      }
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className="onboarding-layertree"
        sx={{
          height: expandAddLayersList
            ? "100%"
            : maximizeTable ||
                (showDescription && editPopover && selectedTableRows)
              ? "0px"
              : !activeLayer && selectedTableRows === undefined
                ? "80%"
                : "40%",

          display: "flex",
          flexDirection: "column",
          gap: 1,
          paddingX: 1.5,

          paddingY: expandAddLayersList
            ? 1.5
            : maximizeTable ||
                (showDescription && editPopover && selectedTableRows)
              ? 0
              : 1.5,

          marginBottom: expandAddLayersList
            ? 0
            : maximizeTable ||
                (showDescription && editPopover && selectedTableRows)
              ? 0
              : 1,

          overflow: "hidden",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , margin 0.3s , padding 0.3s",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "auto",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
              }}
            >
              {t("layers_window.layers_title")}
            </Typography>
            {!editPopover && (
              <IconButton
                className="onboarding-webServices"
                sx={{
                  backgroundColor: darkTheme
                    ? "primary.light"
                    : "darkblue.light",
                  padding: "3px",
                  borderRadius: "2px",
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
                disableRipple={true}
                onClick={handleAddLayersList}
              >
                {!expandAddLayersList && <Add fontSize="small" />}
                {expandAddLayersList && <Remove fontSize="small" />}
              </IconButton>
            )}
          </Box>
          <IconButton
            sx={{
              padding: 0,
              marginY: 1,
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() =>
              monitor(apm, "close-layertree | Layertree", handleClose)
            }
            disableRipple={true}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: expandAddLayersList ? "block" : "none",
            height: "100%",
            overflow: "auto",
          }}
        >
          <AddLayerContent />
        </Box>
        <TreeView
          id="infra3d-layertree"
          aria-label="file system navigator"
          expanded={expandedTopics}
          onNodeToggle={handleTreeToggle}
          sx={{
            overflow: "auto",
            paddingX: 0.5,
            display: expandAddLayersList ? "none" : "block",
          }}
        >
          {!!topic &&
            topic.length > 0 &&
            viewer &&
            viewer.getScene().getLayerHandler().layers.length > 0 &&
            topic.map((children: Topics, index: number) => (
              <React.Fragment key={`${index}_${children.key}`}>
                {children.folder && (
                  <LayertreeGroups
                    changeExportFile={changeExportFile}
                    item={children}
                    indexInput={[index]}
                    layerMenuID={layerMenuID}
                    setLayerMenuID={setLayerMenuID}
                    handleCloseEverythingOnRemoveLayer={
                      handleCloseEverythingOnRemoveLayer
                    }
                  />
                )}
                {!children.folder && (
                  <LayertreeLayers
                    changeExportFile={changeExportFile}
                    children={children}
                    index={[index]}
                    layerMenuID={layerMenuID}
                    setLayerMenuID={setLayerMenuID}
                    length={children.children?.length || 0}
                    handleCloseEverythingOnRemoveLayer={
                      handleCloseEverythingOnRemoveLayer
                    }
                  />
                )}
              </React.Fragment>
            ))}
        </TreeView>
      </Box>
      <Box
        className="onboarding-featureTable"
        sx={{
          height: expandAddLayersList
            ? "0%"
            : maximizeTable ||
                (showDescription && editPopover && selectedTableRows)
              ? "100%"
              : !activeLayer && selectedTableRows === undefined
                ? "20%"
                : "60%",

          minHeight:
            showDescription && editPopover && selectedTableRows
              ? "308px"
              : "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          paddingX: 1.5,
          paddingY: expandAddLayersList ? 0 : 1.5,
          overflow: "auto",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , padding 0.3s",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            color: darkTheme ? "white" : "darkblue.main",

            backgroundColor:
              selectedTableRows === undefined || editPopover
                ? "transparent"
                : darkTheme
                  ? "primary.light"
                  : "darkblue.light",

            transition: "background-color 0.15s",
          }}
        >
          <Box
            sx={{
              width: "100%",
              paddingLeft:
                selectedTableRows === undefined || editPopover ? "0px" : "4px",
              display: "flex",
              gap: 0.5,
              alignItems: "center",
              transition: "padding 0.15s",
            }}
          >
            {selectedTableRows !== undefined && !editPopover && (
              <IconButton
                onClick={handleCloseFeatureInfo}
                disableRipple={true}
                size="small"
                sx={{
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
              >
                <KeyboardBackspace />
              </IconButton>
            )}
            {editPopover && (
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                {t("layers_window.edit_mode")}
                {activeLayer &&
                  activeLayer.title !== "" &&
                  " - " + activeLayer.title}
              </Typography>
            )}
            {!editPopover && (
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                {selectedTableRows === undefined
                  ? t("layers_window.feature_title_table")
                  : t("layers_window.feature_title_info")}
                {activeLayer &&
                  activeLayer.title !== "" &&
                  selectedTableRows === undefined &&
                  " - " + activeLayer.title}
              </Typography>
            )}
          </Box>
          {(activeLayer || selectedTableRows !== undefined) && (
            <Box
              sx={{
                display: "flex",
                gap: "2px",
              }}
            >
              {!editPopover && authMode === "edit" && !showGeonisMask && (
                <>
                  <IconButton
                    disabled={disableEditMode}
                    disableRipple
                    onClick={() =>
                      monitor(
                        apm,
                        "enable-edit-table | Feature Table",
                        enableEdit
                      )
                    }
                    sx={{
                      padding: 0,
                      marginLeft: "auto",
                      color: enableEditFeature
                        ? "secondary.main"
                        : darkTheme
                          ? "white"
                          : "darkblue.main",
                      "@media (hover: hover)": {
                        ":hover": { color: "secondary.main" },
                      },
                    }}
                  >
                    <EditOutlined />
                  </IconButton>
                  {selectedTableRows === undefined && (
                    <IconButton
                      disabled={disableEditMode}
                      disableRipple
                      onClick={() =>
                        monitor(
                          apm,
                          "enable-delete-table | Feature Table",
                          enableDelete
                        )
                      }
                      sx={{
                        padding: 0,
                        marginLeft: "auto",
                        color: enableDeleteFeature
                          ? "secondary.main"
                          : darkTheme
                            ? "white"
                            : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                  {selectedTableRows !== undefined && (
                    <IconButton
                      disabled={disableEditMode}
                      disableRipple
                      onClick={() =>
                        monitor(
                          apm,
                          "delete-feature-table | Feature Table",
                          () =>
                            deleteSingleFeature(String(selectedTableRows.id))
                        )
                      }
                      sx={{
                        padding: 0,
                        marginLeft: "auto",
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                  <IconButton
                    disabled={
                      featureTableAddedRows === undefined ||
                      featureTableAddedRows.length === 0
                    }
                    disableRipple
                    onClick={() =>
                      monitor(
                        apm,
                        "save-changes-table | Feature Table",
                        saveChanges
                      )
                    }
                    sx={{
                      padding: 0,
                      marginLeft: "auto",
                      color: darkTheme ? "white" : "darkblue.main",
                      "@media (hover: hover)": {
                        ":hover": { color: "secondary.main" },
                      },
                    }}
                  >
                    <SaveAsOutlined />
                  </IconButton>
                </>
              )}
              {!showGeonisMask && (
                <IconButton
                  onClick={() =>
                    monitor(
                      apm,
                      "change-table-size | Feature Table",
                      handleChangeTableSize
                    )
                  }
                  disableRipple={true}
                  size="small"
                  sx={{
                    padding: 0,
                    marginLeft: 0.5,
                    color: darkTheme ? "white" : "darkblue.main",
                    "@media (hover: hover)": {
                      ":hover": { color: "secondary.main" },
                    },
                  }}
                  className={editPopover ? "" : "zoominout-button"}
                >
                  {maximizeTable && !editPopover && <ZoomInMap />}
                  {!maximizeTable && !editPopover && <ZoomOutMap />}
                  {showEditToolsButtons && editPopover && (
                    <ChevronLeft sx={{ rotate: "90deg" }} />
                  )}
                  {!showEditToolsButtons && editPopover && (
                    <ChevronRight sx={{ rotate: "90deg" }} />
                  )}
                </IconButton>
              )}
              <IconButton
                onClick={
                  selectedTableRows === undefined
                    ? handleCloseFeatureTable
                    : handleCloseFeatureInfo
                }
                disableRipple={true}
                sx={{
                  padding: 0,
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          )}
        </Box>
        {editPopover && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "auto",
            }}
          >
            {!activeLayer && selectedTableRows === undefined && (
              <Typography
                sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                variant="body2"
              >
                {t("layers_window.edit_message")}
              </Typography>
            )}
            {activeLayer && (
              <EditContent>
                {featureTableAddedRows && (
                  <EditFeatureList
                    rows={
                      editToolActivated === "add"
                        ? featureTableAddedRows.inserted
                        : editToolActivated === "edit"
                          ? featureTableAddedRows.updated
                          : editToolActivated === "delete"
                            ? featureTableAddedRows.deleted
                            : []
                    }
                  />
                )}
              </EditContent>
            )}
          </Box>
        )}
        {!editPopover && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "auto",
            }}
          >
            {!activeLayer && (
              <Typography
                sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                variant="body2"
              >
                {t("layers_window.feature_message")}
              </Typography>
            )}
            {activeLayer && selectedTableRows === undefined && (
              <FeatureListTable
                sorter={featureTableSorter}
                setSorter={setFeatureTableSorter}
                pageNumber={featureTablePageNumber}
                setPageNumber={setFeatureTablePageNumber}
                numberPerPage={featureTableNumberPerPage}
                setNumberPerPage={setFeatureTableNumberPerPage}
              />
            )}
            {activeLayer && selectedTableRows !== undefined && (
              <SingleFeatureTable
                selectedTableRows={selectedTableRows}
                showGeonisMask={showGeonisMask}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
